import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { MessageSquare, ArrowDownLeft, ArrowUpRight, CheckCircle2, XCircle, RefreshCw, Clock } from 'lucide-react';

const DIRECTION_META = {
  inbound:  { icon: ArrowDownLeft, color: 'text-blue-600 bg-blue-50' },
  outbound: { icon: ArrowUpRight, color: 'text-emerald-600 bg-emerald-50' },
  status:   { icon: Clock,        color: 'text-amber-600 bg-amber-50' },
};

const STATUS_COLORS = {
  delivered: 'bg-emerald-100 text-emerald-700',
  sent:      'bg-blue-100 text-blue-700',
  read:      'bg-purple-100 text-purple-700',
  queued:    'bg-gray-100 text-gray-700',
  processed: 'bg-emerald-100 text-emerald-700',
  mock_sent: 'bg-amber-100 text-amber-700',
  failed:    'bg-red-100 text-red-700',
};

export default function AdminWhatsAppInbox() {
  const [filter, setFilter] = useState('all');
  const [messages, setMessages] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, cRes] = await Promise.all([
        api.get(`/webhooks/whatsapp/messages?direction=${filter}&limit=200`),
        api.get('/webhooks/whatsapp/config'),
      ]);
      setMessages(mRes.data.messages || []);
      setConfig(cRes.data || null);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const cleanNumber = (s) => (s || '').replace(/^whatsapp:/i, '').replace(/\s/g, '').trim() || '—';

  return (
    <div data-testid="admin-whatsapp-inbox">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">WhatsApp Inbox</h1>
          <p className="text-sm text-gray-500">Twilio message audit — inbound / outbound / delivery status</p>
        </div>
        <button data-testid="wa-refresh-btn" onClick={load}
          className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-sm hover:bg-gray-50">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {config && (
        <div className="mb-5 flex flex-wrap gap-2 text-xs">
          <span data-testid="wa-config-source"
            className={`font-mono px-2 py-0.5 rounded-sm ${config.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
            {config.is_active ? 'LIVE' : 'INACTIVE'} · source={config.source}
          </span>
          {config.phone_number && (
            <span className="font-mono px-2 py-0.5 rounded-sm bg-gray-50 text-gray-600">{config.phone_number}</span>
          )}
          <span className={`font-mono px-2 py-0.5 rounded-sm ${config.twilio_sdk_available ? 'bg-blue-50 text-blue-700' : 'bg-red-50 text-red-700'}`}>
            SDK: {config.twilio_sdk_available ? 'ok' : 'missing'}
          </span>
        </div>
      )}

      <div className="flex gap-1 mb-4">
        {['all', 'inbound', 'outbound', 'status'].map(d => (
          <button key={d} data-testid={`wa-filter-${d}`} onClick={() => setFilter(d)}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider rounded-sm border transition ${
              filter === d ? 'bg-black text-white border-black' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
            {d}
          </button>
        ))}
      </div>

      <div className="border border-gray-200">
        <table className="w-full" data-testid="wa-messages-table">
          <thead>
            <tr className="border-b border-gray-200 bg-surface">
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Direction</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">SID</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">From</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">To</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Body</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Status</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">When</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400 text-sm">Loading...</td></tr>
            ) : messages.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400 text-sm">
                <MessageSquare className="w-6 h-6 mx-auto mb-2 opacity-40" /> No messages yet
              </td></tr>
            ) : messages.map(m => {
              const meta = DIRECTION_META[m.direction] || DIRECTION_META.inbound;
              const Icon = meta.icon;
              return (
                <tr key={m.id} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                    data-testid={`wa-row-${m.sid || m.id}`} onClick={() => setSelected(m)}>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono uppercase rounded-sm ${meta.color}`}>
                      <Icon className="w-3 h-3" /> {m.direction}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{(m.sid || '').substring(0, 18) || '—'}</td>
                  <td className="px-4 py-3 text-xs">{cleanNumber(m.from)}</td>
                  <td className="px-4 py-3 text-xs">{cleanNumber(m.to)}</td>
                  <td className="px-4 py-3 text-xs max-w-xs truncate">{m.body || (m.error ? <span className="text-red-600">{m.error}</span> : '—')}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-[11px] font-mono rounded-sm ${STATUS_COLORS[m.status] || 'bg-gray-100 text-gray-600'}`}>
                      {m.status || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-[11px] text-gray-400">
                    {m.created_at ? new Date(m.created_at).toLocaleString() : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" data-testid="wa-detail-drawer" onClick={() => setSelected(null)}>
          <aside className="w-[480px] max-w-full bg-white h-full overflow-y-auto border-l border-gray-200 p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-semibold">Message detail</h2>
              <button onClick={() => setSelected(null)} className="text-sm text-gray-500 hover:text-black" data-testid="wa-detail-close">Close</button>
            </div>
            <DetailRow k="SID" v={selected.sid} />
            <DetailRow k="Direction" v={selected.direction} />
            <DetailRow k="Type" v={selected.type} />
            <DetailRow k="From" v={cleanNumber(selected.from)} />
            <DetailRow k="To" v={cleanNumber(selected.to)} />
            <DetailRow k="Status" v={selected.status} />
            <DetailRow k="Case" v={selected.case_id || '—'} mono />
            {selected.error && (
              <div className="mt-4 p-3 border border-red-200 bg-red-50 rounded-sm text-xs text-red-700 flex gap-2">
                <XCircle className="w-4 h-4 flex-shrink-0" /> {selected.error}
              </div>
            )}
            {selected.body && (
              <div className="mt-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1">Body</div>
                <pre className="p-3 bg-gray-50 border border-gray-200 rounded-sm text-xs whitespace-pre-wrap">{selected.body}</pre>
              </div>
            )}
            {(selected.status_history || []).length > 0 && (
              <div className="mt-5">
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Delivery timeline</div>
                <ul className="space-y-1.5">
                  {selected.status_history.map((s, i) => (
                    <li key={i} className="text-xs flex items-center gap-2">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                      <span className="font-mono">{s.status}</span>
                      <span className="text-gray-400">{new Date(s.at).toLocaleString()}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}

function DetailRow({ k, v, mono }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-2 py-1.5 border-b border-gray-100 last:border-0">
      <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 pt-0.5">{k}</div>
      <div className={`text-xs ${mono ? 'font-mono text-gray-600' : 'text-gray-800'} break-all`}>{v || '—'}</div>
    </div>
  );
}
